// Runtime that evaluates enabled SuggestionRule set and shows a suggestion once
// a trigger condition is met on the current page. Coordinates with PGTracker's
// events + tracks its own displays/clicks via /api/track.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { api } from '@/lib/api';
import { trackEvent } from '@/components/PGTracker';

function useIsAdminRoute() {
  const loc = useLocation();
  return loc.pathname.startsWith('/admin');
}

function sessionKey(id) { return `pg_suggestion_seen::${id}::session`; }
function dayKey(id)     { return `pg_suggestion_seen::${id}::day`; }

function destinationHref(rule) {
  if (rule.destination_kind === 'url' && rule.destination_url) return rule.destination_url;
  const ref = rule.destination_ref || '';
  switch (rule.destination_kind) {
    case 'channel':    return ref ? `/${ref.replace(/^\//, '')}` : '/watch';
    case 'product':    return ref ? `/vault/${ref}` : '/vault';
    case 'article':    return ref ? `/on-my-mind/${ref}` : '/on-my-mind';
    case 'audio_item': return ref ? `/music` : '/music';
    case 'video':      return ref ? `/watch` : '/watch';
    case 'collection': return ref ? `/collections/${ref}` : '/collections';
    default:           return '/';
  }
}

function messageFor(rule) {
  if (rule.message_mode === 'custom' && rule.message_custom_text) return rule.message_custom_text;
  const fallback = {
    continue_with: 'Continue with',
    listen:        'Listen to a Podcast',
    explore:       'Explore the Gallery',
    read:          'Continue Reading',
    discover:      'Discover More',
  };
  const base = fallback[rule.message_default_key] || 'Discover more';
  const kind = rule.destination_kind;
  const kindLabel = kind === 'audio_item' ? 'Music' : (kind.charAt(0).toUpperCase() + kind.slice(1));
  return `${base} ${kindLabel}`;
}

export default function SuggestionRuntime() {
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = useIsAdminRoute();

  const [rules, setRules] = useState([]);
  const [active, setActive] = useState(null); // currently shown rule
  const pageStart = useRef(Date.now());
  const pagesViewed = useRef(0);

  // Fetch rules once + on custom refresh
  useEffect(() => {
    if (isAdmin) return undefined;
    let alive = true;
    const fetchRules = () => {
      api.get('/suggestion-rules/active').then(({ data }) => { if (alive) setRules(data || []); }).catch(() => {});
    };
    fetchRules();
    const t = setInterval(fetchRules, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(t); };
  }, [isAdmin]);

  // Reset per page + increment pages_viewed
  useEffect(() => {
    pageStart.current = Date.now();
    pagesViewed.current += 1;
    setActive(null);
  }, [location.pathname]);

  // Filter candidate rules to those that can fire under current frequency policy
  const candidates = useMemo(() => (rules || [])
    .filter((r) => r.enabled)
    .filter((r) => {
      if (r.frequency === 'once_per_session' && sessionStorage.getItem(sessionKey(r.id))) return false;
      if (r.frequency === 'once_per_day') {
        const today = new Date().toISOString().slice(0, 10);
        const stored = localStorage.getItem(dayKey(r.id));
        if (stored === today) return false;
      }
      return true;
    })
    .sort((a, b) => a.priority - b.priority), [rules]);

  const trigger = (rule) => {
    if (active) return; // don't stack
    setActive(rule);
    if (rule.frequency === 'once_per_session') sessionStorage.setItem(sessionKey(rule.id), '1');
    if (rule.frequency === 'once_per_day')     localStorage.setItem(dayKey(rule.id), new Date().toISOString().slice(0, 10));
    trackEvent({ event_type: 'suggestion_display', meta: { rule_id: rule.id, style: rule.display_style } });
  };

  // Evaluate triggers periodically
  useEffect(() => {
    if (isAdmin) return undefined;
    const evalNow = () => {
      if (active || candidates.length === 0) return;
      const elapsedSec = (Date.now() - pageStart.current) / 1000;
      const h = document.documentElement;
      const scrolled = h.scrollTop + window.innerHeight;
      const full = Math.max(h.scrollHeight, 1);
      const scrollPct = Math.min(100, Math.round((scrolled / full) * 100));
      for (const r of candidates) {
        const th = Number(r.trigger_threshold) || 0;
        if (r.trigger === 'time_on_page' && elapsedSec >= th) return trigger(r);
        if (r.trigger === 'scroll_percentage' && scrollPct >= th) return trigger(r);
        if (r.trigger === 'pages_viewed' && pagesViewed.current >= th) return trigger(r);
      }
    };
    const t = setInterval(evalNow, 1500);
    const onScroll = () => evalNow();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { clearInterval(t); window.removeEventListener('scroll', onScroll); };
  }, [candidates, active, isAdmin]);

  // Exit-intent hook
  useEffect(() => {
    if (isAdmin) return undefined;
    const onLeave = (e) => {
      if (active) return;
      if (e.clientY > 3) return;
      const exitRule = candidates.find((r) => r.trigger === 'exit_intent');
      if (exitRule) trigger(exitRule);
    };
    document.addEventListener('mouseout', onLeave);
    return () => document.removeEventListener('mouseout', onLeave);
  }, [candidates, active, isAdmin]);

  if (isAdmin || !active) return null;

  const href = destinationHref(active);
  const label = active.cta_label || 'Continue';
  const msg = messageFor(active);

  const clickThrough = () => {
    trackEvent({ event_type: 'suggestion_click', meta: { rule_id: active.id, destination: href } });
    setActive(null);
    if (/^https?:/i.test(href)) window.location.href = href;
    else navigate(href);
  };

  const dismiss = () => { setActive(null); };

  const containerStyle = (style) => {
    if (style === 'bottom_banner') return 'fixed bottom-0 left-0 right-0 z-40 flex justify-center';
    if (style === 'floating_card') return 'fixed bottom-4 right-4 z-40';
    if (style === 'side_panel')    return 'fixed right-0 top-1/3 z-40';
    if (style === 'inline')        return 'fixed bottom-4 left-1/2 -translate-x-1/2 z-40';
    return 'fixed bottom-4 left-1/2 -translate-x-1/2 z-40'; // slide_up_card default
  };

  const cardStyle = (style) => {
    if (style === 'bottom_banner') return 'w-full bg-[var(--pg-navy-900)] border-t border-[var(--pg-border-2)] p-4 flex items-center justify-center gap-4';
    if (style === 'side_panel')    return 'w-72 bg-[var(--pg-navy-900)] border border-[var(--pg-border-2)] rounded-l-[var(--pg-radius-md)] p-4 shadow-2xl';
    if (style === 'inline')        return 'bg-[var(--pg-navy-900)] border border-[var(--pg-border-2)] rounded-full px-5 py-2 shadow-lg flex items-center gap-3';
    // slide_up_card + floating_card
    return 'bg-[var(--pg-navy-900)] border border-[var(--pg-border-2)] rounded-[var(--pg-radius-md)] p-4 shadow-2xl max-w-sm';
  };

  return (
    <AnimatePresence>
      <motion.div
        key={active.id}
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.25 }}
        className={containerStyle(active.display_style)}
        data-testid={`suggestion-${active.display_style}`}
      >
        <div className={cardStyle(active.display_style)}>
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <p className="font-serif text-base text-[var(--pg-ink)]">{msg}</p>
            </div>
            <button onClick={dismiss} aria-label="dismiss" className="text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)]" data-testid="suggestion-dismiss">
              <X size={14} />
            </button>
          </div>
          <div className="mt-3 flex justify-end">
            <button onClick={clickThrough} data-testid="suggestion-cta" className="pg-btn-primary rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-xs">
              {label}
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
